import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ChatPage from "./ChatPage";
import LoadingSpinner from "../../common/LoadingSpinner";
import ISonicMessage from "../../common/classes/SonicMessage";

import useSocket from "../../services/socket/useSocket";
import SocketClientEvents from "../../services/socket/SocketClientEvents";
import SocketServerEvents from "../../services/socket/SocketServerEvents";
import { getMessageHistories } from "../../services/api/chat/getMessageHistories";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getUserId, getMeta } from "../../redux/reducers/user";
import { getAuthToken } from "../../redux/reducers/auth";
import {
  getStateChatRooms,
  removeNotification,
} from "../../redux/reducers/chat";

const ChatPageContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const auth_token = useAppSelector(getAuthToken);
  const userId = useAppSelector(getUserId);
  const userMeta = useAppSelector(getMeta);

  const socket = useSocket();

  const ChatRooms = useAppSelector(getStateChatRooms);

  const [pastMessages, setPastMessages] = useState<Array<ISonicMessage>>([]);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [clientEvent, setClientEvent] = useState<string>("");

  const dispatch = useAppDispatch();

  const sendMessage = (msg: string, img?: File) => {
    setClientEvent("send");
    socket.emit(
      SocketClientEvents.sendMessage,
      id,
      msg,
      img && { name: img.name, data: img }
    );
  };

  const readMessage = (
    messageIds: Array<string>,
    messages: Array<ISonicMessage>
  ) => {
    const p = [...messages];
    const unreadNumbers: Array<number> = [];

    for (const messageId of messageIds) {
      const i = p.findIndex(({ id }) => id === messageId);
      if (i === -1) continue;

      const id = p[i].unreadUsers.findIndex((el) => el === userId);
      if (id !== -1) unreadNumbers.push(i);
    }

    if (!unreadNumbers.length) return;

    const newArray = p.map((message, index) => {
      if (unreadNumbers.includes(index)) {
        return {
          ...message,
          unreadUsers: message.unreadUsers.filter(
            (unreadUser) => unreadUser !== userId
          ),
        };
      }

      return message;
    });

    for (const number of unreadNumbers) {
      dispatch(removeNotification(p[number].roomId));
      socket.emit(SocketClientEvents.readMessage, p[number].id);
    }

    setPastMessages(newArray);
  };

  const handleLoad = async (page: number) => {
    if (!auth_token || !hasNext || !id) return;

    setClientEvent("load");
    const res = await getMessageHistories(id, auth_token, page);
    if (!res) return;
    setPastMessages((prevMessages) => [...res.messageHistory, ...prevMessages]);
    setHasNext(res.hasNext);
  };

  const recieveMessage = (msg: ISonicMessage) => {
    if (msg.roomId !== id) return;
    setPastMessages([...pastMessages, msg]);
  };

  const setMessageAsRead = (messageId: string, userId: string) => {
    const i = pastMessages.findIndex(({ id }) => id === messageId);
    if (i === -1) return;

    const p = [...pastMessages];
    const userI = p[i].unreadUsers.findIndex((id) => id === userId);

    if (userI === -1) return;

    const newArray = p.map((message, index) => {
      if (i === index) {
        return {
          ...message,
          unreadUsers: message.unreadUsers.filter(
            (unreadUser) => unreadUser !== userId
          ),
        };
      }

      return message;
    });
    setPastMessages(newArray);
  };

  useEffect(() => {
    if (!auth_token || !id) return;
    getMessageHistories(id, auth_token, 1)
      .then((res) => {
        if (res) {
          const messages = [...res.messageHistory];
          setPastMessages(messages);
          setHasNext(res.hasNext);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    socket.on(SocketServerEvents.receivedMessage, recieveMessage);
    socket.on(SocketServerEvents.messageWasRead, setMessageAsRead);
    return () => {
      socket.off(SocketServerEvents.receivedMessage, recieveMessage);
      socket.off(SocketServerEvents.messageWasRead, setMessageAsRead);
    };
  }, [pastMessages]);

  const [chatPartners, setChatPartners] =
    useState<Array<{ id: string; name: string; chat_denial: boolean }>>();
  useEffect(() => {
    if (id && ChatRooms) {
      // get room
      const room = ChatRooms
        ? ChatRooms.find((room) => room.id === id)
        : undefined;

      // get chat partner
      const partners = room
        ? room.users.filter((user) => user.id !== userId)
        : undefined;

      if (partners) {
        setChatPartners(partners);
      }
    }
  }, [id, ChatRooms]);

  if (isLoading || !userId || !chatPartners) {
    return <LoadingSpinner space={5} />;
  } else if (!socket.active || !socket.connected) {
    return (
      // TODO 失敗時の表示
      <div>
        <h1>接続失敗</h1>
      </div>
    );
  } else {
    return (
      <ChatPage
        userId={userId}
        pastMessages={pastMessages}
        hasNext={hasNext}
        clientEvent={clientEvent}
        sendMessageHandler={sendMessage}
        handleLoad={handleLoad}
        readMessage={readMessage}
        setClientEvent={setClientEvent}
        chatPartners={chatPartners}
        sendDisabled={
          userMeta?.chat_denial ||
          chatPartners.length === 0 ||
          (chatPartners.length > 0 && chatPartners[0].chat_denial)
        }
      />
    );
  }
};

export default ChatPageContainer;
